import { makeStyles } from '@material-ui/core/styles';
import typography from '@/theme/typography';
import {
  TextColors,
  TextOpacities,
  SecondaryColors,
  SurfaceColors,
  BackgroundColors,
} from '@/utils/constants';
import { hexToRGBA } from '@/utils/helpers';

const getDarkTheme = (muiTheme: any) => ({
  root: {
    '& $header, $description, $footer': {
      color: hexToRGBA(TextColors.light, TextOpacities.light2),
    },

    '& $title': {
      color: TextColors.light,
    },

    '& $category': {
      color: SecondaryColors.main,

      '&:hover:not(span)': {
        color: SecondaryColors.light,
      },
    },

    '& $chip': {
      '&.MuiChip-root, &.MuiChip-clickable': {
        '&:hover, &:focus': {
          '&:after': {
            backgroundColor: hexToRGBA(SurfaceColors.light, 0.05),
          },
        },
      },
    },

    '&$rootSkeleton': {
      '& .skeleton': {
        background: BackgroundColors.light,
      },
      '&.animated': {
        '& .skeleton': {
          animation: `$skeletonCardDark 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
        },
      },
    },
  },
});

const getLightTheme = (muiTheme: any) => ({
  root: {
    '& $header, $description, $footer': {
      color: hexToRGBA(TextColors.dark, TextOpacities.dark2),
    },

    '& $title': {
      color: TextColors.dark,
    },

    '& $category': {
      color: SecondaryColors.dark,

      '&:hover:not(span)': {
        color: SecondaryColors.main,
      },
    },

    '& $chip': {
      '&.MuiChip-root': {
        '&:hover, &:focus': {
          '&:after': {
            backgroundColor: hexToRGBA(SurfaceColors.dark, 0.05),
          },
        },
      },
    },

    '&$rootSkeleton': {
      '& .skeleton': {
        background: BackgroundColors.light,
      },
      '&.animated': {
        '& .skeleton': {
          animation: `$skeletonCard 1000ms ${muiTheme.transitions.easing.easeInOut} infinite`,
        },
      },
    },
  },
});

const useStyles = makeStyles(
  (muiTheme) => ({
    root: {
      '&.dark-theme': getDarkTheme(muiTheme).root,
      '&.light-theme': getLightTheme(muiTheme).root,
      padding: muiTheme.spacing(4),
      width: '100%',
      position: 'relative',

      '&.MuiCard-root:not(:hover):not(:focus-within)': {
        boxShadow: 'none',
      },

      '& header': {
        '& + *': {
          marginTop: muiTheme.spacing(1),
        },
      },
      '& .footer-partner-only-wrap': {
        marginTop: muiTheme.spacing(2),
        [muiTheme.breakpoints.up('md')]: {
          display: 'none',
        },
      },
    },
    chip: {
      position: 'relative',
      zIndex: 2,

      '&.MuiChip-root, &.MuiChip-clickable': {
        '&:hover, &:focus': {
          '&:after': {
            content: '""',
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            position: 'absolute',
            zIndex: 1,
          },
        },
      },
    },
    cardContent: {
      display: 'flex',
      alignItems: 'flex-start',

      [muiTheme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    button: {
      textAlign: 'left',
      width: '100%',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    header: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',

      '& .search-header': {
        alignItems: 'baseline',
        display: 'flex',
        flexWrap: 'wrap',

        '& > *:not(:last-child)': {
          marginBottom: muiTheme.spacing(1),
          flexShrink: 0,
        },
      },

      '& .header-partner-only': {
        display: 'none',
        [muiTheme.breakpoints.up('md')]: {
          display: 'inline-flex',
        },
      },
    },
    skeleton: {
      height: '11px',
      width: '100%',
      borderRadius: 4,

      '&:before': {
        content: '""',
        fontSize: 'inherit',
        lineHeight: 'inherit',
        display: 'block',
      },

      '&.is-image': {
        width: '100%',
        paddingTop: '65%',
        position: 'absolute',
        top: 0,
        left: 0,
        borderRadius: '4px',

        [muiTheme.breakpoints.up('md')]: {
          width: '230px',
          paddingTop: '149px',
        },
      },
    },
    link: {
      '&.MuiButtonBase-root': {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 1,
      },
    },
    title: {
      '& .skeleton': {
        marginTop: '9px',
        marginBottom: '16px',
        height: '16px',
        width: '75%',

        [muiTheme.breakpoints.up('md')]: {
          width: '30%',
        },
      },
    },
    main: {
      '& + *': {
        marginTop: muiTheme.spacing(2),
      },
    },
    description: {
      ...typography.body1,

      '& .skeleton': {
        '& + *': {
          marginTop: '14px',
        },

        '&:last-child': {
          width: '50%',
        },
      },
    },
    image: {
      width: '100%',
      paddingTop: '65%',
      position: 'relative',
      overflow: 'hidden',
      flexShrink: 0,
      marginBottom: '28px',

      '& $img': {
        position: 'absolute',
        top: 0,
      },

      [muiTheme.breakpoints.up('md')]: {
        width: '230px',
        paddingTop: '149px',
        marginBottom: '0',
        marginRight: '28px',
      },

      '&:after': {
        content: '""',
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        borderRadius: '4px',
        border: '1px solid rgba(0, 0, 0, .05)',
      },

      '&.no-bleeding': {
        [muiTheme.breakpoints.down('sm')]: {
          width: '100vw',
          marginLeft: '-32px',
          marginRight: '-32px',
          marginTop: '-32px',
        },
      },
    },
    img: {},
    spacer: {
      flex: 1,
    },
    footer: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'space-between',

      '& > *': {
        flexGrow: 1,
      },
    },
    category: {
      marginBottom: muiTheme.spacing(1),
      textDecoration: 'none',

      '& .skeleton': {
        height: '10px',
        width: '50px',
      },
    },
    tagsWrapper: {
      display: 'flex',
      marginBottom: muiTheme.spacing(-1),

      '& .title': {
        lineHeight: '2.2em',
      },
    },
    tags: {
      marginLeft: muiTheme.spacing(1),
    },
    date: {
      marginLeft: '8px',
      marginTop: '16px',
      lineHeight: '1.8em',
      textAlign: 'right',
    },
    rootSkeleton: {},
    '@keyframes skeletonCard': {
      '0%, 100%': {
        opacity: 0.75,
      },
      '50%': {
        opacity: 1,
      },
    },
    '@keyframes skeletonCardDark': {
      '0%, 100%': {
        opacity: 0.25,
      },
      '50%': {
        opacity: 0.5,
      },
    },
  }),
  { name: 'GenSearchResultsCard' },
);

export default useStyles;
